// ---- Shared combat render reactions (plan 13) ------------------------------
// The rig-only hit/defeat presentation primitives — the flinch scale pop, the
// whole-body punch lunge, and the defeat-topple curve — extracted from
// src/combat.ts so the game client AND the studio's skills simulator play the
// exact same feel (same constants, same easing; no studio-local copies to
// drift). This module knows nothing about `Live`/`game`/net — it operates on
// bare Object3Ds via WeakMap timers, the repo's standard transient-pose
// pattern (timers eased back to rest, never touching shared materials).
//
// Ordering rule still applies on both sides: call the apply/advance helpers
// AFTER the gait/updatePlayer pass each frame so these overrides win.

import type * as THREE from "three";

export const FLINCH_DUR = 0.18;    // hit-reaction pop length (seconds)
export const FLINCH_POP = 0.16;    // peak extra scale on a flinch
export const DEFEAT_DUR = 0.6;     // collapse (topple) animation length
export const NPC_PUNCH_DUR = 0.35; // rigless-NPC haymaker lunge length

// Transient hit-reaction timers, keyed by the group that got hit.
const flinch = new WeakMap<THREE.Object3D, number>();
// Whole-body punch-lunge timers (rigless NPCs pitch the whole group forward).
const lunge = new WeakMap<THREE.Object3D, number>();

/** Start a hit-reaction pop on any rig/group. */
export function popFlinch(obj: THREE.Object3D): void {
  flinch.set(obj, FLINCH_DUR);
}

/** Ease the hit-reaction scale pop back to 1 over FLINCH_DUR. Call every frame. */
export function applyFlinch(obj: THREE.Object3D, dt: number): void {
  const t = flinch.get(obj);
  if (t === undefined) return;
  const left = t - dt;
  if (left <= 0) { flinch.delete(obj); obj.scale.setScalar(1); return; }
  flinch.set(obj, left);
  obj.scale.setScalar(1 + FLINCH_POP * (left / FLINCH_DUR));
}

/** Start a whole-body haymaker lunge. For **rigless** NPCs only (bugs/quads/birds
 *  /parts models) — jointed humanoids use `startRigAttack` so their unarmed jab
 *  or weapon clip is the attack, not a full-body lean. net.ts gates on `L.rig`. */
export function popLunge(obj: THREE.Object3D): void {
  lunge.set(obj, NPC_PUNCH_DUR);
}

/** Ease the punch lunge (a forward pitch) back to upright. Call every frame. */
export function applyLunge(obj: THREE.Object3D, dt: number): void {
  const t = lunge.get(obj);
  if (t === undefined) return;
  const left = t - dt;
  if (left <= 0) { lunge.delete(obj); obj.rotation.x = 0; return; }
  lunge.set(obj, left);
  const thrust = Math.sin((1 - left / NPC_PUNCH_DUR) * Math.PI); // 0 → 1 → 0
  obj.rotation.x = 0.6 * thrust; // pitch forward into a haymaker
}

/** The defeat-topple curve: progress [0,1] through the fall for `t` seconds in.
 *  Apply as `rotation.z = k * π/2` (tip onto the side) and lift the feet-pivot
 *  group by `k * 0.3` so the side-lying body rests ON the ground, not half-buried. */
export function toppleK(t: number): number {
  return Math.min(1, t / DEFEAT_DUR);
}
